import React, { useCallback, useEffect, useMemo } from "react";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";

import {
	getOwnerExperianSuccessValue,
	getOwnerGidentifySuccessValue,
	getOwnerGofacSuccessValue,
} from "mappers";
import {
	useCheckIfDocumentExistsMutation,
	useDownloadExperianOwnerDocumentMutation,
	useDownloadGidentifyOwnerDocumentMutation,
	useDownloadGofacOwnerDocumentMutation,
	useExperianOwnerAuroraOutcomeFieldMutation,
	useExperianOwnerResultQuery,
	useGidentifyAuroraOutcomeFieldMutation,
	useGidentifyOwnerReRunMutation,
	useGidentifyOwnerResultQuery,
	useGofacAuroraOutcomeFieldMutation,
	useGofacOwnerReRunMutation,
	useGofacOwnerResultQuery,
} from "queries";
import { uiActions } from "store";
import { AuroraOutcomeStatuses } from "types";

import ExperianInfo from "./experianInfo";
import { OwnerHeader } from "./ownerHeader";
import { CheckListRow } from "../checkListRow";

const Owner = ({
	ownerName,
	merchantData,
	ownerType,
	loading,
	setCheckedValues,
	applicationId,
	isDisabledOutcome,
	isEditExperianFields,
	isJarisApplication,
}) => {
	const params = useParams();

	const dispatch = useDispatch();

	const { mutate: checkFullAppDocument } = useCheckIfDocumentExistsMutation();

	const showViewFullAppDocument = () => {
		checkFullAppDocument({
			id: applicationId,
			errorLink: `/underwriting/${params.tab}/${applicationId}/${params.underwritingTab}/error`,
		});
	};

	const showOwner = owner => {
		dispatch(
			uiActions.showModal("MERCHANT", {
				owner,
				handleShowViewFullAppDocument: showViewFullAppDocument,
			}),
		);
	};

	const {
		mutate: downloadGOFACOwnerDocument,
		isLoading: isLoadingDownloadGOFACOwnerDocument,
	} = useDownloadGofacOwnerDocumentMutation();

	const {
		mutate: downloadGidentifyDocument,
		isLoading: isLoadingGidentifyDocument,
	} = useDownloadGidentifyOwnerDocumentMutation();

	const {
		mutate: downloadExperianOwnerDocument,
		isLoading: isLoadingDownloadExperianOwnerDocument,
	} = useDownloadExperianOwnerDocumentMutation();

	const {
		data: dataOwner,
		isFetching: isLoadingDataOwner,
		refetch: dataOwnerRefetch,
		isError: isGofacError,
	} = useGofacOwnerResultQuery({
		id: applicationId,
		ownerType: ownerType,
	});

	const {
		data: dataGidentifyOwner,
		isFetching: isLoadingDataGidentifyOwner,
		refetch: dataGidentifyOwnerRefetch,
		isError: isGidentifyError,
	} = useGidentifyOwnerResultQuery({
		id: applicationId,
		ownerType,
	});

	const {
		data: dataExperianOwner,
		isFetching: isLoadingDataExperianOwner,
		refetch: refetchDataExperianOwner,
		isError: isExperianError,
	} = useExperianOwnerResultQuery({
		id: applicationId,
		ownerType,
	});

	const experianCheckResult = useMemo(
		() => getOwnerExperianSuccessValue(dataExperianOwner),
		[dataExperianOwner],
	);

	const gOFACValues = useMemo(
		() => getOwnerGofacSuccessValue(dataOwner),
		[dataOwner],
	);
	const gidentifyValues = useMemo(
		() => getOwnerGidentifySuccessValue(dataGidentifyOwner),
		[dataGidentifyOwner],
	);

	useEffect(() => {
		setCheckedValues({
			gOFAC:
				gOFACValues?.auroraOutcome === AuroraOutcomeStatuses.pass.name,
			gidentify:
				gidentifyValues?.auroraOutcome ===
				AuroraOutcomeStatuses.pass.name,
			experian:
				experianCheckResult?.auroraOutcome ===
				AuroraOutcomeStatuses.pass.name,
		});
	}, [
		experianCheckResult?.auroraOutcome,
		gOFACValues?.auroraOutcome,
		gidentifyValues?.auroraOutcome,
		setCheckedValues,
	]);

	const {
		mutate: submitGofacAuroraOutcome,
		isLoading: isLoadingGofacAuroraOutcome,
	} = useGofacAuroraOutcomeFieldMutation();

	const {
		mutate: submitGidentifyAuroraOutcome,
		isLoading: isLoadingGidentifyAuroraOutcome,
	} = useGidentifyAuroraOutcomeFieldMutation();

	const {
		mutate: submitExperianAuroraOutcome,
		isLoading: isLoadingExperianAuroraOutcome,
	} = useExperianOwnerAuroraOutcomeFieldMutation();

	const { mutate: reRunGofacOwner, isLoading: isLoadingReRunGofacOwner } =
		useGofacOwnerReRunMutation();

	const {
		mutate: reRunGidentifyOwner,
		isLoading: isLoadingReRunGidentifyOwner,
	} = useGidentifyOwnerReRunMutation();

	const handleReRunGofacOwner = () =>
		reRunGofacOwner({
			id: applicationId,
			onSuccessHandler: dataOwnerRefetch,
			ownerType,
		});

	const handleReRunGidentifyOwner = () =>
		reRunGidentifyOwner({
			id: applicationId,
			onSuccessHandler: dataGidentifyOwnerRefetch,
			ownerType,
		});

	const showFraudPopup = useCallback(() => {
		dispatch(
			uiActions.showModal("EXPERIAN_CHECK_LIST", {
				applicationId,
				ownerType,
				refetchDataExperianOwner,
			}),
		);
	}, [applicationId, dispatch, ownerType, refetchDataExperianOwner]);

	return (
		<div>
			{merchantData?.fullName ? (
				<>
					<OwnerHeader
						ownerName={ownerName}
						merchantData={merchantData}
						showOwner={showOwner}
						loading={loading}
					/>

					<CheckListRow
						checkboxLabel="gOFAC"
						checkboxName="owner-gofac"
						checkResult={gOFACValues}
						isLoading={
							isLoadingDataOwner || isLoadingReRunGofacOwner
						}
						ownerType={ownerType}
						downloadDocument={downloadGOFACOwnerDocument}
						isLoadingDownload={isLoadingDownloadGOFACOwnerDocument}
						submitAuroraOutcome={submitGofacAuroraOutcome}
						isLoadingAuroraOutcome={isLoadingGofacAuroraOutcome}
						refetchData={dataOwnerRefetch}
						applicationId={applicationId}
						isDisabledOutcome={isDisabledOutcome}
						handleRerun={handleReRunGofacOwner}
						isDisabledActions={isGofacError}
					/>

					<CheckListRow
						checkboxLabel="gIdentify"
						checkboxName="owner-gidentify"
						checkResult={gidentifyValues}
						isLoading={
							isLoadingDataGidentifyOwner ||
							isLoadingReRunGidentifyOwner
						}
						ownerType={ownerType}
						downloadDocument={downloadGidentifyDocument}
						isLoadingDownload={isLoadingGidentifyDocument}
						submitAuroraOutcome={submitGidentifyAuroraOutcome}
						isLoadingAuroraOutcome={isLoadingGidentifyAuroraOutcome}
						refetchData={dataGidentifyOwnerRefetch}
						applicationId={applicationId}
						isDisabledOutcome={isDisabledOutcome}
						handleRerun={handleReRunGidentifyOwner}
						isDisabledActions={isGidentifyError}
					/>

					<CheckListRow
						checkboxLabel="Experian"
						checkboxName="experian"
						checkResult={experianCheckResult}
						isLoading={isLoadingDataExperianOwner}
						ownerType={ownerType}
						downloadDocument={downloadExperianOwnerDocument}
						isLoadingDownload={
							isLoadingDownloadExperianOwnerDocument
						}
						submitAuroraOutcome={submitExperianAuroraOutcome}
						refetchData={refetchDataExperianOwner}
						isLoadingAuroraOutcome={isLoadingExperianAuroraOutcome}
						applicationId={applicationId}
						isDisabledOutcome={isDisabledOutcome}
						onFraudClickFn={showFraudPopup}
						isDisabledActions={
							isExperianError ||
							experianCheckResult?.resultMode === "manually_set"
						}
					/>

					<ExperianInfo
						experianData={dataExperianOwner}
						ownerType={ownerType}
						applicationId={applicationId}
						refetchExperianInfo={refetchDataExperianOwner}
						isLoading={isLoadingDataExperianOwner}
						isEditExperianFields={isEditExperianFields}
					/>
				</>
			) : null}
		</div>
	);
};

export default Owner;
